<script setup lang="ts">
import { computed } from 'vue';
import { modalProps } from 'naive-ui';

const props = defineProps({
  ...modalProps,
  /**
   * Dialog height. Content that sizes itself from its host — `FileSelector`'s
   * tab panes are `height: 100%` over a `flex: 1 1 0` chain — collapses to
   * nothing without one, so a file picker has to say how tall it is.
   */
  height: {
    type: String,
    default: undefined,
  },
  /** Dialog width (mock 5a modal is 420px). */
  width: {
    type: String,
    default: '420px',
  },
});

// `width` is ours and `preset` is fixed to card — keep both off n-modal.
const passThrough = computed(() => {
  const { width, height, preset, ...rest } = props;
  return rest;
});
</script>

<template>
  <n-modal
    v-bind="passThrough"
    preset="card"
    class="gaia-modal"
    :content-scrollable="!props.height"
    :style="{ width: props.width, height: props.height }"
  >
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>
    <slot />
    <template v-if="$slots.action" #action>
      <slot name="action" />
    </template>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </n-modal>
</template>

<!--
  Not scoped: n-modal teleports the card to <body>, where a scoped selector
  can't reach it.

  A dialog never grows past the window. With `content-scrollable` (above),
  naive then keeps the title and the action row pinned and scrolls only the
  content between them, in its own scrollbar — so a long form on a small
  screen never pushes Save out of reach. Dialogs that set a `height` lay out
  their own content (a file picker's full-height panes) and are left alone.
-->
<style>
.n-card.gaia-modal {
  max-width: calc(100vw - 32px);
  max-height: calc(100dvh - 48px);
}
</style>
